import React from 'react'
import axios, { AxiosHeaders, AxiosRequestConfig, InternalAxiosRequestConfig, Method } from 'axios'
import Cookies from 'js-cookie'
import { toast } from 'react-hot-toast'

import { FileRequestConfig, ToastAxiosConfig } from '@/lib/types/utilsT'
import { DEFAULT_ERROR_MESSAGES } from '@/constants/utils'
import { SESSION_COOKIE_KEY, SUPER_ADMIN_SESSION_COOKIE_KEY } from '@/constants/session'
import { appendFormData, camelize, snakeize } from '@/lib/utils'
import Notify from '@/components/Notify'

type AuthArea = 'admin' | 'super-admin'

type AuthAxiosConfig = {
  authArea?: AuthArea
  // Opts a request out of the automatic Authorization header — for endpoints that are
  // public/anonymous by contract (e.g. the live-contact preview), so a session cookie
  // that happens to exist in the browser (tenant or super-admin) isn't sent along.
  skipAuth?: boolean
}

type AppAxiosConfig = AxiosRequestConfig & ToastAxiosConfig & FileRequestConfig & AuthAxiosConfig

const PATHNAME_HEADER = 'x-pathname'

// --- Axios instance ---
export const axiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
  timeout: 10000,
})

function isSuperAdminPath(pathname?: string | null): boolean {
  if (!pathname) return false

  return pathname === '/super-admin' || pathname.startsWith('/super-admin/')
}

function getCookieKeyByAuthArea(authArea: AuthArea) {
  return authArea === 'super-admin' ? SUPER_ADMIN_SESSION_COOKIE_KEY : SESSION_COOKIE_KEY
}

async function getServerPathname(): Promise<string> {
  try {
    const { headers } = await import('next/headers')
    const headersList = await headers()

    return headersList.get(PATHNAME_HEADER) ?? ''
  } catch {
    return ''
  }
}

async function getServerToken(authArea?: AuthArea): Promise<string | undefined> {
  const { cookies } = await import('next/headers')

  const cookieStore = await cookies()
  const pathname = await getServerPathname()

  const resolvedAuthArea: AuthArea =
    authArea ?? (isSuperAdminPath(pathname) ? 'super-admin' : 'admin')

  const cookieKey = getCookieKeyByAuthArea(resolvedAuthArea)

  return cookieStore.get(cookieKey)?.value
}

function getClientToken(authArea?: AuthArea): string | undefined {
  const pathname = window.location.pathname

  const resolvedAuthArea: AuthArea =
    authArea ?? (isSuperAdminPath(pathname) ? 'super-admin' : 'admin')

  const cookieKey = getCookieKeyByAuthArea(resolvedAuthArea)

  return Cookies.get(cookieKey)
}

async function getAuthToken(authArea?: AuthArea): Promise<string | undefined> {
  if (typeof window === 'undefined') {
    return getServerToken(authArea)
  }

  return getClientToken(authArea)
}

function setAuthorizationHeader(config: InternalAxiosRequestConfig, token: string) {
  const headers = AxiosHeaders.from(config.headers)

  headers.set('Authorization', `Bearer ${token}`)

  config.headers = headers
}

// Attach Authorization header
axiosInstance.interceptors.request.use(async (config) => {
  const headers = AxiosHeaders.from(config.headers)

  if (headers.has('Authorization')) {
    config.headers = headers
    return config
  }

  const { authArea, skipAuth } = config as InternalAxiosRequestConfig & AuthAxiosConfig

  if (skipAuth) {
    config.headers = headers
    return config
  }

  const token = await getAuthToken(authArea)

  if (token) {
    setAuthorizationHeader(config, token)
  }

  return config
})

// --- Helpers ---
const getNotifyContent = (message: string, type: 'success' | 'error' | 'pending') => (
  <Notify
    iconLoading={type === 'pending'}
    iconCheck={type === 'success'}
    iconError={type === 'error'}
  >
    {message}
  </Notify>
)

const triggerToast = (
  type: 'success' | 'error' | 'pending',
  message?: string,
  id?: string,
  duration?: number
) => {
  if (!message) return

  return type === 'success'
    ? toast(getNotifyContent(message, type), { id, duration })
    : toast(getNotifyContent(message, type), { duration })
}

const resolveErrorMessage = (
  error: any,
  { errorMessage, customErrorMessages, customErrorMessagesByCode }: ToastAxiosConfig
): string => {
  if (error?.response) {
    const { status, data } = error.response
    const code = data?.code

    if (typeof code === 'number' && customErrorMessagesByCode?.[code]) {
      return customErrorMessagesByCode[code]
    }

    if (typeof status === 'number' && customErrorMessages?.[status]) {
      return customErrorMessages[status]
    }

    if (DEFAULT_ERROR_MESSAGES[status]) {
      return DEFAULT_ERROR_MESSAGES[status]
    }
  }

  return errorMessage || error?.message || 'An error occurred.'
}

const handleFileDownload = (
  data: BlobPart,
  fileName?: string,
  options?: {
    type?: string
    autoOpen?: boolean
  }
) => {
  const blobOptions: BlobPropertyBag = {
    type: options?.type || 'application/octet-stream',
  }

  const blob = new Blob([data], blobOptions)
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = fileName || 'file'

  if (options?.type) {
    link.setAttribute('type', options.type)
  }

  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

// --- Core request ---
async function requestWithToast<T = any>(
  method: Method,
  url: string,
  data?: any,
  config?: AppAxiosConfig
): Promise<T> {
  const {
    showToast,
    successMessage,
    errorMessage,
    pendingMessage,
    customErrorMessages,
    customErrorMessagesByCode,
    fileName,
    isDownload,
    fileOptions,
    toastDuration,
    authArea,
    skipAuth,
    ...axiosConfig
  } = config || {}

  const toastEnabled = !!showToast

  const pendingId =
    toastEnabled && pendingMessage
      ? toast(getNotifyContent(pendingMessage, 'pending'), {
          duration: toastDuration?.loading,
        })
      : undefined

  try {
    const response = await axiosInstance.request<{
      status: string
      data: T | BlobPart
    }>({
      method,
      url,
      data: data instanceof FormData ? data : snakeize(data),
      authArea,
      skipAuth,
      ...axiosConfig,
    } as AppAxiosConfig)

    if (response.data instanceof Blob && isDownload) {
      handleFileDownload(response.data, fileName, fileOptions)
    }

    if (pendingId) {
      triggerToast('success', successMessage, pendingId, toastDuration?.success)
    } else if (toastEnabled) {
      triggerToast('success', successMessage, undefined, toastDuration?.success)
    }

    return camelize(response.data.data)
  } catch (err: any) {
    const message = resolveErrorMessage(err, {
      errorMessage,
      customErrorMessages,
      customErrorMessagesByCode,
    })

    if (toastEnabled) {
      if (pendingId) toast.dismiss(pendingId)

      triggerToast('error', message, undefined, toastDuration?.error)
    }

    if (err.message && message) {
      err.message = message
    }

    throw err
  }
}

// --- Exported helpers ---
export const getRequest = <T = any,>(url: string, params?: any, config?: AppAxiosConfig) =>
  requestWithToast<T>('GET', url, undefined, {
    ...config,
    params: params ? snakeize(params) : undefined,
  })

export const postRequest = <T = any,>(url: string, data?: any, config?: AppAxiosConfig) =>
  requestWithToast<T>('POST', url, data, config)

export const putRequest = <T = any,>(url: string, data?: any, config?: AppAxiosConfig) =>
  requestWithToast<T>('PUT', url, data, config)

export const deleteRequest = <T = any,>(url: string, data?: any, config?: AppAxiosConfig) =>
  requestWithToast<T>('DELETE', url, data, config)

export const uploadRequest = <T = any,>(
  url: string,
  method: Method,
  data: Record<string, any>,
  config?: AppAxiosConfig
) => {
  const formData = new FormData()

  appendFormData(formData, snakeize(data))

  return requestWithToast<T>(method, url, formData, config)
}
